import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  Button,
  IconButton,
  TextField,
  FormControlLabel,
  Switch,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowBack, FileDownload, CheckCircle, Remove, ExpandMore } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import QuadrimestreSelector, { getQuadrimestreAtual, formatQuadrimestreParam } from '../../components/saude/QuadrimestreSelector';
import HelpButton from '../../components/saude/HelpButton';

interface Paciente {
  co_seq_fat_cidadao_pec: number;
  nu_cpf_cidadao: string;
  nu_cns: string;
  no_cidadao: string;
  dt_nascimento: string;
  idade: number;
  sexo: string;
  ine: string;
  equipe: string;
  unidade: string;
  score_total: number;
  [key: string]: any;
}

interface Paginacao {
  page: number;
  limit: number;
  total: number;
  total_paginas: number;
}

interface EquipeInfo {
  ine: string;
  equipe: string;
  unidade: string;
  // Loaded data
  pacientes?: Paciente[];
  paginacao?: Paginacao;
  loading?: boolean;
  page?: number;
}

interface IndicatorCol {
  key: string;
  label: string;
  dateKey?: string;
}

const INDICATOR_COLUMNS: { [eixo: string]: IndicatorCol[] } = {
  'c1-mais-acesso': [],
  'c2-criancas': [
    { key: 'ind_a', label: '1a Consulta 30d' },
    { key: 'ind_b', label: '9+ Consultas' },
    { key: 'ind_c', label: 'Antropometria' },
    { key: 'ind_d', label: 'Visitas ACS' },
    { key: 'ind_e', label: 'Vacinas' },
  ],
  'c3-gestantes': [
    { key: 'ind_a', label: 'Inicio Precoce' },
    { key: 'ind_b', label: '7+ Consultas' },
    { key: 'ind_c', label: 'PA' },
    { key: 'ind_d', label: 'Antropometria' },
    { key: 'ind_e', label: 'Visitas ACS' },
    { key: 'ind_f', label: 'dTpa' },
    { key: 'ind_g', label: 'Exames 1oTri' },
    { key: 'ind_h', label: 'Exames 3oTri' },
    { key: 'ind_i', label: 'Cons. Puerp.' },
    { key: 'ind_j', label: 'Visita Puerp.' },
    { key: 'ind_k', label: 'Saude Bucal' },
  ],
  'c4-diabeticos': [
    { key: 'ind_a', label: 'Consulta', dateKey: 'dt_ultima_consulta' },
    { key: 'ind_b', label: 'PA', dateKey: 'dt_ultima_pa' },
    { key: 'ind_c', label: 'Antropometria', dateKey: 'dt_ultima_antropometria' },
    { key: 'ind_d', label: 'Visitas ACS', dateKey: 'dt_ultima_visita' },
    { key: 'ind_e', label: 'HbA1c', dateKey: 'dt_ultimo_hba1c' },
    { key: 'ind_f', label: 'Pe Diabetico', dateKey: 'dt_ultimo_pe_diabetico' },
  ],
  'c5-hipertensos': [
    { key: 'ind_a', label: 'Consulta', dateKey: 'dt_ultima_consulta' },
    { key: 'ind_b', label: 'PA', dateKey: 'dt_ultima_pa' },
    { key: 'ind_c', label: 'Antropometria', dateKey: 'dt_ultima_antropometria' },
    { key: 'ind_d', label: 'Visitas ACS', dateKey: 'dt_ultima_visita' },
  ],
  'c6-idosos': [
    { key: 'ind_a', label: 'Consulta', dateKey: 'dt_ultima_consulta' },
    { key: 'ind_b', label: 'Antropometria', dateKey: 'dt_ultima_antropometria' },
    { key: 'ind_c', label: 'Visitas ACS', dateKey: 'dt_ultima_visita' },
    { key: 'ind_d', label: 'Influenza', dateKey: 'dt_ultima_influenza' },
  ],
  'c7-mulheres': [
    { key: 'ind_a', label: 'Citopatologico', dateKey: 'dt_ultima_citopat' },
    { key: 'ind_b', label: 'HPV', dateKey: 'dt_ultima_hpv' },
    { key: 'ind_c', label: 'Saude Sexual', dateKey: 'dt_ultima_saude_sexual' },
    { key: 'ind_d', label: 'Mamografia', dateKey: 'dt_ultima_mamografia' },
  ],
  'eapp-gestacao': [
    { key: 'ind_a', label: '3+ Consultas' },
    { key: 'ind_b', label: 'Testes IST' },
    { key: 'ind_c', label: 'dTpa' },
    { key: 'ind_d', label: '3+ PA' },
    { key: 'ind_e', label: 'Saude Bucal' },
  ],
  'eapp-diabetes-has': [
    { key: 'ind_a', label: 'Consulta', dateKey: 'dt_ultima_consulta' },
    { key: 'ind_b', label: 'PA', dateKey: 'dt_ultima_pa' },
    { key: 'ind_c', label: 'Antropometria', dateKey: 'dt_ultima_antropometria' },
    { key: 'ind_d', label: 'HbA1c', dateKey: 'dt_ultima_hba1c' },
  ],
  'eapp-ist': [
    { key: 'ind_a', label: 'HIV' },
    { key: 'ind_b', label: 'Sifilis' },
    { key: 'ind_c', label: 'Hep. B' },
    { key: 'ind_d', label: 'Hep. C' },
  ],
  'eapp-cancer': [
    { key: 'ind_a', label: 'Citopatologico', dateKey: 'dt_ultima_citopat' },
  ],
  'ecr-gestacao': [
    { key: 'ind_a', label: '3+ Consultas' },
    { key: 'ind_b', label: 'Testes IST' },
    { key: 'ind_c', label: 'dTpa' },
    { key: 'ind_d', label: '3+ PA' },
    { key: 'ind_e', label: 'Saude Bucal' },
  ],
  'ecr-ist': [
    { key: 'ind_a', label: 'HIV' },
    { key: 'ind_b', label: 'Sifilis' },
    { key: 'ind_c', label: 'Hep. B' },
    { key: 'ind_d', label: 'Hep. C' },
  ],
  'ecr-tuberculose': [
    { key: 'ind_a', label: 'Consulta' },
    { key: 'ind_b', label: 'Baciloscopia' },
    { key: 'ind_c', label: 'DOT' },
    { key: 'ind_d', label: 'Visita ACS' },
  ],
  'eapp-tuberculose': [
    { key: 'ind_a', label: 'Consulta Med/Enf' },
    { key: 'ind_b', label: 'Baciloscopia' },
    { key: 'ind_c', label: 'DOT' },
    { key: 'ind_d', label: 'Visita Domiciliar' },
  ],
  'nt30-vinculo': [
    { key: 'ind_cadastro', label: 'Cadastro (MICI)' },
    { key: 'ind_acompanhamento', label: 'Acompanhamento 12m' },
    { key: 'visitas_acs', label: 'Visitas ACS', dateKey: 'dt_ultima_visita' },
  ],
};

function maskCpf(cpf: string | null | undefined): string {
  if (!cpf) return '---';
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length < 5) return '***.***.***-**';
  const lastFive = cleaned.slice(-5);
  return `***.***${lastFive.length >= 5 ? '.' + lastFive.slice(0, 3) + '-' + lastFive.slice(3) : lastFive}`;
}

function formatDate(dt: string | null | undefined): string {
  if (!dt) return '';
  try {
    const d = new Date(dt);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return ''; }
}

const SaudeListaNominal: React.FC = () => {
  const { eixo } = useParams<{ eixo: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const initialIne = searchParams.get('ine') || '';
  const [agrupado, setAgrupado] = useState(true);
  const [equipesList, setEquipesList] = useState<EquipeInfo[]>([]);
  const [expandedIne, setExpandedIne] = useState<string>(initialIne);

  // Flat mode state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<Paciente[]>([]);
  const [eixoNome, setEixoNome] = useState('');
  const [paginacao, setPaginacao] = useState<Paginacao>({ page: 1, limit: 50, total: 0, total_paginas: 0 });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedIne, _setSelectedIne] = useState<string>(initialIne);
  const [page, setPage] = useState(1);
  const [searchName, setSearchName] = useState('');
  const [somenteIncompletos, setSomenteIncompletos] = useState(false);

  const qAtual = getQuadrimestreAtual();
  const [quadrimestre, setQuadrimestre] = useState(qAtual.quadrimestre);
  const [ano, setAno] = useState(qAtual.ano);
  const qParam = formatQuadrimestreParam(quadrimestre, ano);

  const handleQuadChange = (q: string, a: number) => { setQuadrimestre(q); setAno(a); setPage(1); };

  const indicators = eixo ? INDICATOR_COLUMNS[eixo] || [] : [];

  // Fetch equipes list for grouped mode
  const fetchEquipes = useCallback(async () => {
    if (!municipioAtivo?.id) return;
    try {
      const result = await saudeService.getEquipes(municipioAtivo.id);
      setEquipesList((result || []).map((eq: any) => ({
        ine: eq.ine,
        equipe: eq.equipe || eq.nome,
        unidade: eq.unidade,
      })));
    } catch (err) { console.error('Erro ao carregar equipes:', err); }
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade]);

  // Fetch data for flat mode or a specific equipe in grouped mode
  const fetchListaNominal = useCallback(async (ine?: string, pg = 1) => {
    if (!municipioAtivo?.id || !eixo) return null;
    const result = await saudeService.getListaNominal(eixo, municipioAtivo.id, pg, 50, ine || undefined, qParam, unidadeSelecionada?.codunidade);
    return result;
  }, [municipioAtivo?.id, eixo, qParam, unidadeSelecionada?.codunidade]);

  // Flat mode fetch
  const fetchFlat = useCallback(async () => {
    if (!municipioAtivo?.id || !eixo) return;
    setLoading(true); setError('');
    try {
      const result = await fetchListaNominal(selectedIne, page);
      if (result) {
        setData(result.pacientes || []);
        setEixoNome(result.nome || eixo);
        setPaginacao(result.paginacao || { page: 1, limit: 50, total: 0, total_paginas: 0 });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar lista nominal');
      setData([]);
    } finally { setLoading(false); }
  }, [municipioAtivo?.id, eixo, page, selectedIne, fetchListaNominal]);

  useEffect(() => { fetchEquipes(); }, [fetchEquipes]);
  useEffect(() => { if (!agrupado) fetchFlat(); }, [agrupado, fetchFlat]);

  // When expanding an equipe accordion, lazy-load its data
  const handleAccordionExpand = async (ine: string) => {
    if (expandedIne === ine) { setExpandedIne(''); return; }
    setExpandedIne(ine);
    const eq = equipesList.find((e) => e.ine === ine);
    if (eq && !eq.pacientes) {
      setEquipesList((prev) => prev.map((e) => e.ine === ine ? { ...e, loading: true } : e));
      try {
        const result = await fetchListaNominal(ine, 1);
        if (result) {
          setEquipesList((prev) => prev.map((e) =>
            e.ine === ine ? { ...e, pacientes: result.pacientes || [], paginacao: result.paginacao, loading: false, page: 1 } : e
          ));
        }
      } catch {
        setEquipesList((prev) => prev.map((e) => e.ine === ine ? { ...e, loading: false } : e));
      }
    }
  };

  // Paginate within equipe accordion
  const handleEquipePage = async (ine: string, newPage: number) => {
    setEquipesList((prev) => prev.map((e) => e.ine === ine ? { ...e, loading: true } : e));
    try {
      const result = await fetchListaNominal(ine, newPage);
      if (result) {
        setEquipesList((prev) => prev.map((e) =>
          e.ine === ine ? { ...e, pacientes: result.pacientes || [], paginacao: result.paginacao, loading: false, page: newPage } : e
        ));
      }
    } catch {
      setEquipesList((prev) => prev.map((e) => e.ine === ine ? { ...e, loading: false } : e));
    }
  };

  // Auto-expand pre-selected equipe
  useEffect(() => {
    if (initialIne && agrupado && equipesList.length > 0) {
      handleAccordionExpand(initialIne);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipesList.length]);

  // Client-side filtering (search + somente incompletos) for flat mode
  const filteredData = data.filter((p) => {
    if (searchName && !p.no_cidadao?.toLowerCase().includes(searchName.toLowerCase())) return false;
    if (somenteIncompletos && indicators.length > 0) {
      const allMet = indicators.every((ind) => p[ind.key] === 1 || p[ind.key] === true);
      if (allMet) return false;
    }
    return true;
  });

  if (!municipioAtivo) {
    return (<Box sx={{ p: 3 }}><Alert severity="warning">Selecione um municipio.</Alert></Box>);
  }

  const renderPatientTable = (patients: Paciente[], showEquipeCol = true) => (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: indicators.length > 5 ? 1200 : 800 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 }}>Nome</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>CPF</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Idade</TableCell>
            {showEquipeCol && <TableCell sx={{ fontWeight: 600 }}>Equipe</TableCell>}
            {indicators.map((ind) => {
              const helpKey = ind.key.replace('ind_', '').toUpperCase();
              return (
                <TableCell key={ind.key} sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="center">
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                    {ind.label}
                    <HelpButton eixo={eixo!} indicador={helpKey} size={14} />
                  </Box>
                </TableCell>
              );
            })}
            <TableCell sx={{ fontWeight: 600 }} align="center">Score</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {patients.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4 + indicators.length + (showEquipeCol ? 1 : 0)} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>Nenhum paciente encontrado.</Typography>
              </TableCell>
            </TableRow>
          ) : (
            patients.map((paciente) => (
              <TableRow key={paciente.co_seq_fat_cidadao_pec} hover>
                <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, maxWidth: 200 }}>
                  <Typography variant="body2" noWrap>{paciente.no_cidadao}</Typography>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{maskCpf(paciente.nu_cpf_cidadao)}</TableCell>
                <TableCell>{paciente.idade}</TableCell>
                {showEquipeCol && (
                  <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>{paciente.equipe}</Typography></TableCell>
                )}
                {indicators.map((ind) => {
                  const val = paciente[ind.key];
                  const isNumeric = typeof val === 'number' && val !== 0 && val !== 1;
                  const met = val === 1 || val === true || (typeof val === 'number' && val > 0);
                  const dateVal = ind.dateKey ? formatDate(paciente[ind.dateKey]) : '';
                  return (
                    <TableCell key={ind.key} align="center">
                      <Tooltip title={ind.label + (dateVal ? ` - ${dateVal}` : '') + (isNumeric ? `: ${val}` : '')} arrow>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                          {isNumeric ? (
                            <Typography variant="body2" fontWeight={600} sx={{ color: val >= 2 ? 'success.main' : 'warning.main' }}>{val}</Typography>
                          ) : met ? (
                            <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
                          ) : (
                            <Remove sx={{ color: 'text.disabled', fontSize: 18 }} />
                          )}
                          {dateVal && <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1 }}>{dateVal}</Typography>}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  );
                })}
                <TableCell align="center">
                  <Chip label={paciente.score_total ?? 0} size="small"
                    sx={{
                      fontWeight: 600, minWidth: 40,
                      bgcolor: paciente.score_total >= 75 ? '#e8f5e9' : paciente.score_total >= 50 ? '#fff3e0' : '#fce4ec',
                      color: paciente.score_total >= 75 ? '#2e7d32' : paciente.score_total >= 50 ? '#e65100' : '#c62828',
                    }} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate(`/saude/${eixo}`)} size="small"><ArrowBack /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
          {eixoNome || eixo} <Typography component="span" variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>- Lista Nominal</Typography>
        </Typography>
        <Button variant="outlined" startIcon={<FileDownload />} size="small"
          onClick={() => { if (municipioAtivo && eixo) saudeService.exportarCsv(eixo, municipioAtivo.id, selectedIne || undefined, qParam, unidadeSelecionada?.codunidade); }}>
          Exportar CSV
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={<Switch checked={agrupado} onChange={(e) => { setAgrupado(e.target.checked); if (!e.target.checked) fetchFlat(); }} size="small" />}
          label={<Typography variant="body2">Agrupar por equipe</Typography>}
        />

        {!agrupado && (
          <>
            <TextField size="small" placeholder="Buscar por nome..." variant="outlined"
              value={searchName} onChange={(e) => setSearchName(e.target.value)} sx={{ minWidth: 200 }} />
            {indicators.length > 0 && (
              <FormControlLabel
                control={<Switch checked={somenteIncompletos} onChange={(e) => setSomenteIncompletos(e.target.checked)} size="small" />}
                label={<Typography variant="body2">Somente pendentes</Typography>}
              />
            )}
          </>
        )}

        <Box sx={{ ml: 'auto' }}>
          <QuadrimestreSelector quadrimestre={quadrimestre} ano={ano} onChange={handleQuadChange} />
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Grouped mode */}
      {agrupado ? (
        equipesList.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          equipesList.map((eq) => (
            <Accordion
              key={eq.ine}
              expanded={expandedIne === eq.ine}
              onChange={() => handleAccordionExpand(eq.ine)}
              disableGutters
              sx={{
                mb: 1, borderRadius: '8px !important', overflow: 'hidden',
                border: '1px solid', borderColor: 'divider',
                '&:before': { display: 'none' }, boxShadow: 'none',
                '&.Mui-expanded': { margin: '0 0 8px 0 !important' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{eq.equipe}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>{eq.unidade}</Typography>
                  </Box>
                  {eq.paginacao && (
                    <Chip label={`${eq.paginacao.total} pacientes`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {eq.loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
                ) : eq.pacientes ? (
                  <>
                    {renderPatientTable(eq.pacientes, false)}
                    {eq.paginacao && eq.paginacao.total_paginas > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                        <Pagination count={eq.paginacao.total_paginas} page={eq.page || 1}
                          onChange={(_, v) => handleEquipePage(eq.ine, v)} size="small" color="primary" />
                      </Box>
                    )}
                  </>
                ) : null}
              </AccordionDetails>
            </Accordion>
          ))
        )
      ) : (
        /* Flat mode */
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            {renderPatientTable(filteredData)}
            {paginacao.total_paginas > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={paginacao.total_paginas} page={page} onChange={(_, v) => setPage(v)} color="primary" showFirstButton showLastButton />
              </Box>
            )}
          </>
        )
      )}
    </Box>
  );
};

export default SaudeListaNominal;
